import { IconButton, Modal, Text, type ModalSize } from "@carbon6/c6-frontend-design";
import { useId, type ReactNode } from "react";
import { hatcheryTextModes } from "@/app/theme";
import { HatModalHeader } from "./HatModalHeader";
import styles from "./HatTextModal.module.css";

export type HatTextModalProps = {
  // 표시 여부(controlled)다. c6 Modal 과 같다.
  open: boolean;
  // 제목.
  label: string;
  // 제목 위 작은 식별자(선택)다. 예: 리소스 이름("Embodiment · DSR").
  eyebrow?: string;
  // 확정/취소 콜백이다. backdrop 클릭·Esc·X 는 기본적으로 onCancelPress 에 매핑한다(busy 면 잠금).
  onOkPress: () => void;
  onCancelPress: () => void;
  // dismiss(X·Esc·backdrop) 전용 콜백이다. 생략하면 onCancelPress 와 같다.
  //
  // 취소 버튼이 "아무것도 안 함"이 아니라 하나의 선택(예: 편집을 되돌린다)일 때만 쓴다.
  // 그때 dismiss 도 그 선택으로 처리하면, 창만 닫으려던 사람이 편집을 잃는다.
  onDismiss?: () => void;
  // 본문 설명(선택)이다. `<p>` 안에 들어가므로 인라인 콘텐츠만 넣는다. 목록·박스처럼 블록
  // 마크업이 필요하면 `content` 를 쓴다.
  caption?: ReactNode;
  // caption 아래 블록 슬롯(선택)이다. 안내 박스·체크박스처럼 `<p>` 에 넣을 수 없는 구조에 쓴다.
  content?: ReactNode;
  // 폭(선택, 기본 sm)이다. 본문에 구조가 있으면 md 이상으로 넓힌다.
  size?: ModalSize;
  // true 면 확정 버튼을 에러(danger) 색으로 표시한다(선택, 기본 false).
  error?: boolean;
  // 서버 에러 등 alert 문구(선택)다. 있으면 caption 아래에 표시한다.
  errorMessage?: string;
  // 확정/취소 버튼 라벨이다(선택, 기본 확인/취소).
  okText?: string;
  cancelText?: string;
  // 제출 중 상태다(선택, 기본 false). true 면 버튼 비활성, 확정 pending, backdrop/Esc 잠금을 적용한다.
  busy?: boolean;
  // 확정 버튼만 비활성으로 둔다(선택, 기본 false). 본문에서 먼저 입력을 받아야 확정할 수 있을 때 쓴다.
  // `busy` 와 달리 취소·dismiss 는 허용한다.
  okDisabled?: boolean;
};

// 수정/삭제 등 확정 액션의 공용 확인 모달이다: 제목 + 설명(선택) + 취소/확정 2버튼.
// error=true 면 확정 버튼이 danger 색이 된다.
export function HatTextModal({
  open,
  label,
  eyebrow,
  onOkPress,
  onCancelPress,
  onDismiss,
  caption,
  content,
  size = "sm",
  error = false,
  errorMessage,
  okText = "확인",
  cancelText = "취소",
  busy = false,
  okDisabled = false,
}: HatTextModalProps): ReactNode {
  const titleId = useId();
  // busy 중에는 dismiss(backdrop·Esc·X)를 잠근다. 그 외에는 전용 핸들러(없으면 취소)로 전달한다.
  function handleDismiss(): void {
    if (!busy) {
      (onDismiss ?? onCancelPress)();
    }
  }

  return (
    <Modal
      open={open}
      onClose={handleDismiss}
      size={size}
      closeOnBackdropClick={!busy}
      closeOnEscape={!busy}
      aria-labelledby={titleId}
    >
      <HatModalHeader title={label} titleId={titleId} close={{ disabled: busy }} />
      <div className={styles.body}>
        {eyebrow ? (
          <Text variant={hatcheryTextModes["caption-bold"]} className={styles.eyebrow}>
            {eyebrow}
          </Text>
        ) : null}
        {caption ? (
          <Text variant={hatcheryTextModes["body-normal"]} className={styles.caption}>
            {caption}
          </Text>
        ) : null}
        {content}
        {errorMessage ? (
          <Text variant={hatcheryTextModes["body-normal"]} className={styles.error} role="alert">
            {errorMessage}
          </Text>
        ) : null}
        <div className={styles.actions}>
          <IconButton
            text={cancelText}
            type="button"
            variant="surface"
            disabled={busy}
            onClick={onCancelPress}
          />
          <IconButton
            text={busy ? `${okText}…` : okText}
            type="button"
            variant="solid"
            tone={error ? "danger" : "primary"}
            disabled={busy || okDisabled}
            onClick={onOkPress}
          />
        </div>
      </div>
    </Modal>
  );
}
